import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { AdditionalInfo, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { finalize, map } from 'rxjs/operators';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and additional information to collect for an event
 */
@Injectable()
export class AdditionalInfoService {
    public static INCLUDE_DIVISIONS: string = 'divisions';

    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all additional information for the specified event
     */
    public getIndex(
        eventId: string,
        includes: string[] = null,
        params: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<AdditionalInfo[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, params);

        return this.http.getPaged(AdditionalInfo, apiRequestUrl, forceRefresh)
            .pipe(map((pagedResponse: PagedResponse) => pagedResponse.recordList));
    }

    /**
     * Get a single additionalInfo record
     */
    public get(eventId: string, additionalInfoId: string, includes: string[]): Observable<AdditionalInfo> {
        const apiRequestUrl: string = this.getApiUrl(eventId, additionalInfoId, includes);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new AdditionalInfo().deserialize(response, response.included))));
    }

    /**
     * Add or update the additionalInfo
     */
    public save(additionalInfo: AdditionalInfo, includes: string[] = null): Observable<AdditionalInfo> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(additionalInfo.eventId, additionalInfo.id, includes);

        if (additionalInfo.id) {
            // Update existing AdditionalInfo
            response = this.http.patch(apiRequestUrl, additionalInfo.serialize());
        } else {
            // Add a new AdditionalInfo
            response = this.http.post(apiRequestUrl, additionalInfo.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new AdditionalInfo()).deserialize(response, response.included)),
                finalize(() => this.http.clearCache(AdditionalInfo)),
            );
    }

    /**
     * Delete the specified additionalInfo record
     */
    public delete(eventId: string, additionalInfoId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, additionalInfoId, null);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.http.clearCache(AdditionalInfo)),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        additionalInfoId: string,
        includes: string[],
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/additional-info/`;

        if (additionalInfoId) {
            url += additionalInfoId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
